import { isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  model,
  output,
  PLATFORM_ID,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { LucideSend } from '@lucide/angular';
import { NgpTextarea } from 'ng-primitives/textarea';
import { TextDirective } from '../../typography/text.directive';
import { ButtonComponent } from '../button/button.component';
import { InputLLMVariant, InputLlmSubmitSource, type InputLlmSubmitEvent } from './input-llm-models';

@Component({
  selector: 'joii-input-llm',
  templateUrl: './input-llm.component.html',
  styleUrls: ['./input-llm.component.css'],
  imports: [NgpTextarea, ButtonComponent, TextDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.data-variant]': 'variant()',
    '[attr.data-multiline]': 'isMultiline() ? true : null',
    '[style.--input-llm-max-auto-resize-rows]': 'maxAutoResizeRows()',
    '[attr.data-focused]': 'focused()',
    '[attr.data-error]': 'error() ? true : null',
    '(focusin)': 'onFocusin()',
    '(focusout)': 'onFocusout($event)',
  },
})
export class InputLlmComponent {
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly platformId = inject(PLATFORM_ID);

  readonly textareaEl = viewChild<ElementRef<HTMLTextAreaElement>>('textareaEl');
  readonly placeholder = input<string>('');
  readonly variant = input<InputLLMVariant>('simple');
  readonly error = input<boolean>(false);
  readonly disabled = input<boolean>(false);
  readonly value = model<string>('');
  /** Caps visible height when using the `simple-auto-resize` variant (`max-height` in line-heights). */
  readonly maxAutoResizeRows = input(5);
  readonly focused = signal(false);
  readonly sendIcon = LucideSend;
  readonly isMultiline = signal(false);

  /** True when the primary pointer is coarse (e.g. touch), used for Enter/newline behaviour. */
  readonly isMobile = signal(false);

  readonly sendButtonClicked = output<InputLlmSubmitEvent>();

  constructor() {
    const coarsePrimary =
      isPlatformBrowser(this.platformId) &&
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(pointer: coarse)').matches;
    this.isMobile.set(coarsePrimary);
  }

  private readonly syncMultilineWithValue = effect(() => {
    this.value();
    queueMicrotask(() => {
      untracked(() => this.updateMultiline());
    });
  });

  onFocusin(): void {
    this.focused.set(true);
  }

  onFocusout(event: FocusEvent): void {
    if (!this.hostEl.nativeElement.contains(event.relatedTarget as Node)) {
      this.focused.set(false);
    }
  }

  protected onValueInput(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    this.value.set(el.value);
  }

  protected onTextareaKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    if (this.isMobile()) return;
    if (event.shiftKey) return;
    if (this.disabled()) return;
    event.preventDefault();
    this.emitSubmit('enter-key');
  }

  protected onSendButtonClick(): void {
    this.emitSubmit('send-button');
  }

  private emitSubmit(source: InputLlmSubmitSource): void {
    if (this.disabled()) return;
    this.sendButtonClicked.emit({
      value: this.value(),
      source,
    });
    this.value.set('');
  }

  protected updateMultiline(): void {
    const el = this.textareaEl()?.nativeElement;
    if (!el) return;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20;
    this.isMultiline.set(el.scrollHeight > lineHeight * 1.5);
  }
}
